import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Feature } from '@igo2/geo';
import { Constantes } from 'src/app/shared/constantes';
import {
  LocalisationTransaction,
  MrcTransaction, MunicipaliteTransaction, RegionAdministrativeTransaction, StatutTransaction
  , TypeTransaction
} from 'src/app/shared/models/transaction-gdf';
import { RechercheGdfService } from 'src/app/shared/services/recherche-gdf.service';
import { SIGTRechercheGdfBaseComponent } from '../_sigt-recherche-gdf.base.component';
import Filter from 'ol/format/filter/Filter';
import { and, equalTo, or } from 'ol/format/filter';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import * as olstyle from 'ol/style';
import { EntityTableTemplate } from '@igo2/common/entity';
import { LanguageService } from '@igo2/core/language';
import { MessageService } from '@igo2/core/message';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FilterableMatSelectDirective } from 'src/app/shared/directives/filterable-mat-select.directive';
import { TranslateModule } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'app-transaction-gdf',
    templateUrl: './transaction-gdf.component.html',
    styleUrls: ['./transaction-gdf.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        MatInputModule,
        MatFormFieldModule,
        MatSelectModule,
        MatButtonModule,
        FilterableMatSelectDirective,
    ]
})
export class TransactionGdfComponent extends SIGTRechercheGdfBaseComponent implements OnInit {
  propertyKeyAndTitle: { [key: string]: string };
  template: EntityTableTemplate;

  #configService = inject(ZzzConfigService);
  form: UntypedFormGroup;
  messageApplication: { [key: string]: string } = {};
  statutListeDeroulante: StatutTransaction[] = [];
  typeTransactionListeDeroulante: TypeTransaction[] = [];
  municipaliteListeDeroulante: MunicipaliteTransaction[] = [];
  mrcListeDeroulante: MrcTransaction[] = [];
  regionsAdministrativeListeDeroulante: RegionAdministrativeTransaction[] = [];
  listeLocalisations: any[] = [];

  get urlBaseApi(): string {
    return this.#configService.get().authApiEndPoint + "/sigtapi";
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    languageService: LanguageService,
    rechercherGdfService: RechercheGdfService,
    httpClient: HttpClient,
    cdRef: ChangeDetectorRef,
    messageService: MessageService
  ) {
    super(rechercherGdfService, httpClient, messageService, languageService, cdRef);
    this.propertyKeyAndTitle = this.rechercherGdfService.getPropertyKeyAndTitle('transactionGdf');
    this.template =  this.rechercherGdfService.getEntityTableTemplate('transactionGdf');
  }

  ngOnInit(): void {
    this.style = new olstyle.Style({
      stroke: new olstyle.Stroke({
        width: 1,
        color: [255, 255, 255, 1]
      }),
      fill: new olstyle.Fill({
        color: [16, 155, 247, 1]
      }),
      image: new olstyle.Circle({
        radius: 4.5,
        stroke: new olstyle.Stroke({
          width: 1,
          color: [255, 255, 255, 1]
        }),
        fill: new olstyle.Fill({
          color: [16, 155, 247, 1]
        })
      })
    });
    this.getLayer("Transaction");
    this.form = this.formBuilder.group({
      typeTransaction: ["", [Validators.required]],
      statut: [""],
      municipalite: [""],
      mrc: [""],
      regionAdministrative: [""]
    }, { validators: [this.validerLocalisation.bind(this)] });

    this.messageApplication.grilleVide = Constantes.MessageApplicatif.AucunElement_6I;
    this.messageApplication.erreurCouche = Constantes.MessageApplicatif.CoucheIndisponible_16T;
    this.messageApplication.obligatoireTypeTransaction = Constantes.MessageApplicatif.ValeurObligatoire_1E
      .replace("{0}", this.languageService.translate.instant('sigt.integration.rechercheGdf.form.transactionGdf.typeTransaction'));

    this.chargerTypesTransactions();
    this.chargerStatus();

  }

  chargerTypesTransactions() {
    this.httpClient.get<{ listeTypeTransaction: TypeTransaction[] }>(this.urlBaseApi + "/gdf/transaction/types-transaction"
    ).subscribe({
      next: reponse => {
        this.typeTransactionListeDeroulante = reponse.listeTypeTransaction
          .sort((a, b) => a.descriptionTypeTransaction.localeCompare(b.descriptionTypeTransaction));
      },
      error: error => {
        this.messageService.error("ERREUR APPEL gdf/transaction/types-transaction");
        console.log(error);
      }
    });
  }

  chargerStatus() {
    //Les status sont chargés une seule fois
    this.httpClient.get<{ listeStatutSuivi: StatutTransaction[] }>(this.urlBaseApi + "/gdf/transaction/statuts")
      .subscribe({
        next: reponse => {
          this.statutListeDeroulante = reponse.listeStatutSuivi.sort((a, b) => a.nomStatut.localeCompare(b.nomStatut));
        },
        error: error => {
          this.messageService.error("ERREUR APPEL gdf/transaction/statuts");
          console.log(error);
        }
      });
  }

  chargerLocalisations(selectedTypeTransaction: string[], selectedStatus: string[]) {
    this.httpClient.post<{ listeLocalisations: LocalisationTransaction[] }>(this.urlBaseApi + "/gdf/transaction/localisations",
      {
        "criteresTypeTransaction": selectedTypeTransaction,
        //"criteresStatutSuivi": selectedStatus
      }
    ).subscribe({
      next: reponse => {
        this.listeLocalisations = reponse.listeLocalisations;

        let distinctRegions = reponse.listeLocalisations.reduce((prev, el) => {
          if (prev.some(o => o.codeRegion === el.codeRegion && o.nomRegion === el.nomRegion))
            return prev;
          return [...prev, { codeRegion: el.codeRegion, nomRegion: el.nomRegion }];
        }, []
        ).sort((a, b) => Number(a.codeRegion) - Number(b.codeRegion));

        distinctRegions = distinctRegions.map(({ codeRegion, nomRegion }) => (
          {
            codeRegion,
            nomRegion: (codeRegion.length === 2 ? codeRegion : "0" + codeRegion) + ' - ' + nomRegion
          }
        ));
        this.regionsAdministrativeListeDeroulante = distinctRegions;

        this.mrcListeDeroulante = reponse.listeLocalisations
        .filter(el => el.codeMrc !== '-') //SIGT-1568
        .reduce((prev, el) => {
          if (prev.some(o => o.codeMrc === el.codeMrc && o.nomMrc === el.nomMrc))
            return prev;
          return [...prev, { codeMrc: el.codeMrc, nomMrc: el.nomMrc }];
        }, []
        ).sort((a, b) => a.nomMrc.localeCompare(b.nomMrc));

        this.municipaliteListeDeroulante = reponse.listeLocalisations
          .map(({ codeMunicipalite, nomMunicipalite }) => ({ codeMunicipalite, nomMunicipalite }))
          .sort((a, b) => a.nomMunicipalite.localeCompare(b.nomMunicipalite));

        let selectionRegionsEncoreValide;
        if (this.form.get("regionAdministrative").value) {
          selectionRegionsEncoreValide = Object.values(this.form.get("regionAdministrative").value).filter(x => {
            if (this.regionsAdministrativeListeDeroulante.some(item => item.codeRegion === x))
              return x;
          });
        }
        this.form.get("regionAdministrative").setValue(selectionRegionsEncoreValide);

        let selectionMrcEncoreValide;
        if (this.form.get("mrc").value) {
          selectionMrcEncoreValide = Object.values(this.form.get("mrc").value).filter(x => {
            if (this.mrcListeDeroulante.some(item => item.codeMrc === x))
              return x;
          });
        }
        this.form.get("mrc").setValue(selectionMrcEncoreValide);

        let selectionMuniEncoreValide;
        if (this.form.get("municipalite").value) {
          selectionMuniEncoreValide = Object.values(this.form.get("municipalite").value).filter(x => {
            if (this.municipaliteListeDeroulante.some(item => item.codeMunicipalite === x))
              return x;
          });
        }
        this.form.get("municipalite").setValue(selectionMuniEncoreValide);

        // this.form.get("regionAdministrative").setValue("");
        // this.form.get("mrc").setValue("");
        // this.form.get("municipalite").setValue("");
      },
      error: error => {
        this.messageService.error("ERREUR APPEL gdf/transaction/localisations");
        console.log(error);
      }
    });
  }

  selectTypeTransactionChanged() {
    this.chargerLocalisations(this.form.get("typeTransaction").value, this.form.get("statut").value);
  }

  selectMrcChange() {
    let localisationFiltrer = this.listeLocalisations;
    if (this.form.get("mrc").value && (Object.values(this.form.get("mrc").value)).length > 0) {
      localisationFiltrer = this.listeLocalisations.filter(x => {
        if ((Object.values(this.form.get("mrc").value)).some(item => item === x.codeMrc))
          return x;
      });
    }
    if (this.form.get("regionAdministrative").value && (Object.values(this.form.get("regionAdministrative").value)).length > 0) {
      localisationFiltrer = localisationFiltrer.filter(x => {
        if ((Object.values(this.form.get("regionAdministrative").value)).some(item => item === x.codeRegion))
          return x;
      });
    }

    this.municipaliteListeDeroulante = localisationFiltrer.map(
      ({ codeMunicipalite, nomMunicipalite }) => ({ codeMunicipalite, nomMunicipalite }))
      .sort((a, b) => a.nomMunicipalite.localeCompare(b.nomMunicipalite));

    let selectionMuniEncoreValide;
    if (this.form.get("municipalite").value) {
      selectionMuniEncoreValide = Object.values(this.form.get("municipalite").value).filter(x => {
        if (this.municipaliteListeDeroulante.some(item => item.codeMunicipalite === x))
          return x;
      });
    }
    this.form.get("municipalite").setValue(selectionMuniEncoreValide);
  }

  selectRegionChange() {
    let localisationFiltrerRegion = this.listeLocalisations;
    if (this.form.get("regionAdministrative").value && (Object.values(this.form.get("regionAdministrative").value)).length > 0) {
      localisationFiltrerRegion = this.listeLocalisations.filter(x => {
        if ((Object.values(this.form.get("regionAdministrative").value)).some(item => item === x.codeRegion))
          return x;
      });
    }
    this.mrcListeDeroulante = localisationFiltrerRegion
    .filter(el => el.codeMrc !== '-') //SIGT-1568
    .reduce((prev, el) => {
      if (prev.some(o => o.codeMrc === el.codeMrc && o.nomMrc === el.nomMrc))
        return prev;
      return [...prev, { codeMrc: el.codeMrc, nomMrc: el.nomMrc }];
    }, []
    ).sort((a, b) => a.nomMrc.localeCompare(b.nomMrc));

    if (this.form.get("mrc").value && (Object.values(this.form.get("mrc").value)).length > 0) {
      let selectionMrcEncoreValide;
      selectionMrcEncoreValide = Object.values(this.form.get("mrc").value).filter(x => {
        if (this.mrcListeDeroulante.some(item => item.codeMrc === x)) return x;
      });
      this.form.get("mrc").setValue(selectionMrcEncoreValide);
      this.selectMrcChange();
    }
    else {
      this.municipaliteListeDeroulante = localisationFiltrerRegion.map(
        ({ codeMunicipalite, nomMunicipalite }) => ({ codeMunicipalite, nomMunicipalite }))
        .sort((a, b) => a.nomMunicipalite.localeCompare(b.nomMunicipalite));
      let selectionMuniEncoreValide;
      if (this.form.get("municipalite").value) {
        selectionMuniEncoreValide = Object.values(this.form.get("municipalite").value).filter(x => {
          if (this.municipaliteListeDeroulante.some(item => item.codeMunicipalite === x)) return x;
        });
      }
      this.form.get("municipalite").setValue(selectionMuniEncoreValide);
    }
  }

  formaterChampsFeaturePourResultatRecherche(features: Feature[]): Feature[] {
    return this.rechercherGdfService.formatFeaturesForTable('transactionGdf',features);
  }


  buildFilter(): Filter {
    let filter: Filter;
    let subFilters: Filter[] = [];

    var typeTransaction = this.form.get("typeTransaction").value;
    var statut = this.form.get("statut").value;
    var municipalite = this.form.get("municipalite").value;
    var mrc = this.form.get("mrc").value;
    var regionAdministrative = this.form.get("regionAdministrative").value;

    {
      let eqFilters = typeTransaction.map(t => equalTo('TTR_TYPE', t));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }
    if (statut !== undefined && statut.length > 0) {
      let eqFilters = statut.map(f => equalTo('TRS_STATU', f));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }

    if (municipalite !== undefined && municipalite.length > 0) {
      let eqFilters = municipalite.map(m => equalTo('MUN_NO', m));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }
    else if (mrc !== undefined && mrc.length > 0) {
      let eqFilters = mrc.map(m => equalTo('MRC_NO', m));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }
    else if (regionAdministrative !== undefined && regionAdministrative.length > 0) {
      let eqFilters = regionAdministrative.map(r => equalTo('REA_NO', r));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFilters.push(filtersOr);
    }

    filter = subFilters.length > 1 ? and(...subFilters) : subFilters[0];

    return filter;
  }

  validerLocalisation(control: FormGroup): any {
    const municipalite = control.controls["municipalite"].value;
    const mrc = control.controls["mrc"].value;
    const regionAdministrative = control.controls["regionAdministrative"].value;

    if ((municipalite?.length > 0 || mrc?.length > 0 || regionAdministrative?.length > 0)) {
      return null;
    }
    return { notValid: true };
  }

  initialiserChamps(): void {
    this.form.setValue({
      typeTransaction: '',
      statut: '',
      municipalite: '',
      mrc: '',
      regionAdministrative: ''
    });
    this.municipaliteListeDeroulante = [];
    this.mrcListeDeroulante = [];
    this.form.get("statut").setValue('');
    this.regionsAdministrativeListeDeroulante = [];
    this.form.get('typeTransaction').setErrors(null, { emitEvent: false });
  }

}
